import { Injectable } from '@angular/core';
import { AlertButton, AlertController, LoadingController, ToastController } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class AppUtilsService {
  private loading: HTMLIonLoadingElement | null = null;

  constructor(
    private loadingController: LoadingController,
    private toastController: ToastController,
    private alertController: AlertController,
  ) { }

  // Loading Methods
  async showLoading(message: string = 'กำลังโหลด...') { // Dismiss any existing loading first
    await this.hideLoading();
    this.loading = await this.loadingController.create({
      message,
      spinner: 'circular',
      cssClass: 'custom-loading',
      backdropDismiss: false,
    });
    await this.loading.present();
  }

  async hideLoading() {
    if (this.loading) {
      await this.loading.dismiss();
      this.loading = null;
    }
  }

  // Toast Methods
  async showToast(options: {
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    position?: 'top' | 'middle' | 'bottom';
  }) {
    const { message, type = 'info', duration = 3000, position = 'bottom' } = options;

    const toast = await this.toastController.create({
      message,
      duration,
      position,
      cssClass: `toast-${type}`,
      buttons: [
        {
          text: 'ปิด',
          role: 'cancel',
        },
      ],
    });

    await toast.present();
    return toast;
  }

  // Shorthand methods for different toast types
  async showSuccess(message: string) {
    return this.showToast({
      message,
      type: 'success',
    });
  }

  async showError(message: string) {
    return this.showToast({
      message,
      type: 'error',
      duration: 3000, // Longer duration for errors
    });
  }

  async showWarning(message: string) {
    return this.showToast({
      message,
      type: 'warning',
    });
  }

  async showInfo(message: string) {
    return this.showToast({
      message,
      type: 'info',
    });
  }

  async presentAlertConfirm(options: {
    header: string;
    message: string;
    handler: () => void;
    handlerCancle?: () => void;
  }) {
    const { header, message, handler, handlerCancle } = options;
    const alertButtons: AlertButton[] = [
      {
        text: 'ตกลง',
        handler: () => {
          handler?.();
        },
      },
    ];

    if (handlerCancle) {
      alertButtons.unshift({
        text: 'ยกเลิก',
        cssClass: 'secondary',
        handler: () => {
          handlerCancle?.();
        },
      });
    }

    const alert = await this.alertController.create({
      header,
      message,
      cssClass: 'custom-alert-cancel-confirm',
      backdropDismiss: false,
      buttons: alertButtons,
    });

    await alert.present();
  }

  public isVersionLessThan(
    currentVersion: string,
    targetVersion: string,
  ): boolean {
    const current = currentVersion.split('.').map(Number);
    const target = targetVersion.split('.').map(Number);

    const length = Math.max(current.length, target.length);

    for (let i = 0; i < length; i++) {
      const currentPart = current[i] ?? 0;
      const targetPart = target[i] ?? 0;

      if (currentPart < targetPart) return true;
      if (currentPart > targetPart) return false;
    }

    return false;
  }
}
